import { STEP_IDS } from 'client-portal/services/widget-steps';
import { action } from '@ember/object';
import { didCancel } from 'ember-concurrency';
import { service } from '@ember/service';
import { trackAppointmentStep } from 'client-portal/utils/event-tracking';
import PersistAppointmentMixin from '../-mixins/persist-appointment';
import Route from '@ember/routing/route';
import classic from 'ember-classic-decorator';

@classic
export default class RequestInformationIndexRoute extends Route.extend(PersistAppointmentMixin) {
  @service analytics;
  @service prescreener;
  @service session;
  @service request;
  @service stripeCards;
  @service currentPractice;

  templateName = '-request/information/index';

  beforeModel() {
    if (this.prescreener.preview.isPreview) return;

    let reservation = this.modelFor(this.request.baseRouteName);
    if (!reservation.get('isReserved')) {
      this.request.transitionTo(this, 'date');
    }
  }

  afterModel() {
    super.afterModel(...arguments);
    if (this.prescreener.preview.isPreview) return;

    let appointment = this.getAppointment();
    if (appointment.client && !appointment.client.isNew) {
      return this.persistAppointmentTask.perform();
    }
  }

  @action
  async nextStep(fromPath = '', stripeElements) {
    let isDesktopNextStep = fromPath === 'desktop';
    let { reorderedNavEnabled } = this.session;

    if (reorderedNavEnabled) {
      await this.validateFieldsAndTransition('message', stripeElements);
    } else {
      if (isDesktopNextStep) {
        try {
          await this.validateAndPersistAppointmentTask.perform(stripeElements);
        } catch (e) {
          if (!didCancel(e)) {
            // Validation failures bubble up as real errors.
            // However, in tests the task can still be "on deck" (e.g. persist not yet canceled)
            // when the route is torn down. Ember Concurrency throws a cancellation error in that case.
            // We ignore cancellations but rethrow anything else so real issues aren't hidden.
            throw e;
          }
        }
      } else {
        await this.validateFieldsAndTransition('information.review', stripeElements);
      }
    }
  }

  async validateFieldsAndTransition(nextRoute, stripeElements) {
    try {
      await this.validateFieldsTask.perform(stripeElements);
      let reservation = this.modelFor(this.request.baseRouteName);
      reservation.set('hasCompletedInformationStep', true);
      this.request.transitionTo(this, nextRoute);
    } catch (err) {
      // noop
    }
  }

  @action
  didTransition() {
    let controller = this.controllerFor(this.request.baseRouteName);
    controller.updateNavigationSteps();

    let appointment = this.getAppointment();
    if (appointment.get('card')) {
      appointment.set('card.card', null);
    }

    let reservation = this.modelFor(this.request.baseRouteName);
    trackAppointmentStep({
      analytics: this.analytics,
      session: this.session,
      request: this.request,
      pageId: STEP_IDS.CLIENT_INFORMATION,
      reservation,
    });

    return true;
  }

  setupController(controller) {
    super.setupController(...arguments);
    controller.setProperties({
      channel: this.modelFor(this.request.baseRouteName).channel,
    });
    if (this.prescreener.isPrescreenerEnabled) {
      let getInformationModel = this.getInformationModel.bind(this);
      let reservation = this.modelFor(this.request.baseRouteName);
      controller.setProperties({ getInformationModel, reservation });

      let allowedClientTypes = this.currentPractice.get('clientTypesAllowedToBookAppt');
      if (allowedClientTypes?.length === 1) {
        controller.onClientTypeChange(allowedClientTypes.firstObject);
      }
    }
  }
}
